import { createApi } from "@reduxjs/toolkit/query/react";
import type {
  SignUpRequest,
  SignUpResponse,
  LoginRequest,
  LoginResponse,
  OtpResponse,
  OtpRequest,
  ResendOtpResponse,
  ResendOtpRequest,
  ForgotPasswordResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ResetPasswordResponse,
  VerifyAuthResponse,
} from "./types";
import { HTTP_METHODS, USER_ROUTES } from "@cointervue/shared/constants";
import { createBaseQueryWithReauth, USER_MGMT } from "@cointervue/shared/api";
import { logout } from "../../store/authSlice";

export const authApi = createApi({
  reducerPath: "authApi",
  baseQuery: createBaseQueryWithReauth(USER_MGMT.href, logout),
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, LoginRequest>({
      query: (credentials) => ({
        url: USER_ROUTES.login,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: credentials,
      }),
    }),

    verifyAuth: builder.query<VerifyAuthResponse, void>({
      query: () => ({
        url: USER_ROUTES.verify,
        method: HTTP_METHODS.GET,
        headers: {
          accept: "*/*",
        },
      }),
    }),

    signup: builder.mutation<SignUpResponse, SignUpRequest>({
      query: (credentials) => ({
        url: USER_ROUTES.signup,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: credentials,
      }),
    }),

    // VALIDATE OTP API
    otpValidate: builder.mutation<OtpResponse, OtpRequest>({
      query: ({ context, ...Credential }) => ({
        url: USER_ROUTES.otpValidate,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        params: {
          context,
        },
        body: Credential,
      }),
    }),

    //RESEND OTP API
    resendOtp: builder.mutation<ResendOtpResponse, ResendOtpRequest>({
      query: ({ context, ...credentials }) => ({
        url: USER_ROUTES.resendOtp,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        params: {
          context,
        },
        body: credentials,
      }),
    }),

    //Forgot Password Api
    forgotPassword: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordRequest
    >({
      query: (credentials) => ({
        url: USER_ROUTES.forgotPassword,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: credentials,
      }),
    }),

    resetPassword: builder.mutation<
      ResetPasswordResponse,
      ResetPasswordRequest
    >({
      query: (Credential) => ({
        url: USER_ROUTES.resetPassword,
        method: HTTP_METHODS.POST,
        headers: {
          accept: "*/*",
          "Content-Type": "application/json",
        },
        body: Credential,
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: USER_ROUTES.logout,
        method: HTTP_METHODS.DELETE,
        headers: {
          accept: "application/json",
        },
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyAuthQuery,
  useLazyVerifyAuthQuery,
  useSignupMutation,
  useResendOtpMutation,
  useForgotPasswordMutation,
  useOtpValidateMutation,
  useResetPasswordMutation,
  useLogoutMutation,
} = authApi;
